import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {serialize} from '@mintdata/mint-kit/impl/util/serializr/src/serializr';
import Dexie from 'dexie';

const DEFAULT_MAX_ROWS = 20000;

interface CachedValue
{
    key:string;
    value:any;
}

class CachedValuesDatabase extends Dexie
{
    public values:Dexie.Table<CachedValue, string>;

    public constructor(dbName:string)
    {
        super(dbName);
        this.version(1).stores({
            values: 'key'
        });
        this.values = this.table('values');
    }
}

export interface CacheInfo
{
    name:string,
    kind:string,
    keyCount:number
}

export interface CacheEntry
{
    key:string,
    value:IValue
}

export interface CacheSetting
{
    name:string,
    value:IValue
}

export interface ICacheScopeProvider
{
    getScope():string;
}

export class CacheController
{
    private cacheDbs:{[dbName:string]:CachedValuesDatabase} = {};

    private _cacheDb(cacheName:string):CachedValuesDatabase
    {
        let dbName = this._cacheNameToDbName(cacheName);
        let db = this.cacheDbs[dbName];
        if( !db )
        {
            db = new CachedValuesDatabase(dbName);
            this.cacheDbs[dbName] = db;
        }
        return db;
    }

    private _cacheNameToDbName(cacheName:string):string
    {
        const scope = commonControllers.context.cacheScope();
        return `__cache_for_${scope}_${cacheName}`;
    }

    private _dbNameToCacheName(dbName:string):string | null
    {
        const scope = commonControllers.context.cacheScope();
        const match = dbName.match(/^__cache_for_([^_]+)_(.*)$/);
        return match && match[1] === scope && match[2];
    }

    public async getCacheValue(cacheName:string, cacheKey:string):Promise<IValue>
    {
        const db = this._cacheDb(cacheName);
        const cached = await db.values.get(cacheKey);
        return cached === undefined ? AEmpty.Value : Type.deserializeIValue(cached.value);
    }

    public async setCacheValue(cacheName:string, cacheKey:string, value:IValue):Promise<ABoolean>
    {
        const serializedValue = serialize(value);

        const db = this._cacheDb(cacheName);
        const ok = await db.values.put({key: cacheKey, value: serializedValue});

        return ABoolean.true();
    }

    public async clearCacheValue(cacheName:string, cacheKey:string):Promise<ABoolean>
    {
        const db = this._cacheDb(cacheName);
        const ok = await db.values.delete(cacheKey);

        return ABoolean.true();
    }

    public async listCaches():Promise<Array<CacheInfo>>
    {
        const databaseNames = await Dexie.getDatabaseNames();
        const cacheNames = databaseNames
            .map(databaseName => this._dbNameToCacheName(databaseName))
            .filter(cacheName => !!cacheName);

        return Promise.all(cacheNames.map(async (cacheName) =>
        {
            const keyCount = await this._cacheDb(cacheName).values.count();
            return {
                name: cacheName,
                kind: 'regular',
                keyCount
            } as CacheInfo;
        }));
    }

    public async getCacheKeys(cacheName:string, maxRows:number = 0):Promise<Array<string>>
    {
        const db = this._cacheDb(cacheName);
        return db.values
            .limit(maxRows > 0 ? maxRows : DEFAULT_MAX_ROWS)
            .primaryKeys();
    }

    public async getCacheEntries(cacheName:string, maxRows:number = 0):Promise<Array<CacheEntry>>
    {
        const db = this._cacheDb(cacheName);

        const result = [];

        await db.values
            .limit(maxRows > 0 ? maxRows : DEFAULT_MAX_ROWS)
            .each(({key, value}) =>
                result.push({key, value: Type.deserializeIValue(value)})
            );

        return result;
    }

    public async getCacheSettings(cacheName:string):Promise<Array<CacheSetting>>
    {
        throw new Error('not implemented');
        // //todo: check that database exists
        // const cacheSize = await this.getCacheSize(cacheName);
        // return [{
        //     name: 'cacheSize',
        //     value: new ANumber(cacheSize)
        // }];
    }

    public async setCacheSize(cacheName:string, maxCacheSize:number):Promise<boolean>
    {
        throw new Error('not implemented');
    }

    public async getCacheSize(cacheName:string):Promise<number>
    {
        throw new Error('not implemented');
    }

    public async deleteCache(cacheName:string):Promise<boolean>
    {
        const dbName = this._cacheNameToDbName(cacheName);

        delete this.cacheDbs[dbName];

        await Dexie.delete(dbName);

        return true;
    }
}
